import {
  slugify,
  textToHtml,
  type CreateInsurancePricingTierRequest,
  type CreateInsuranceRequest,
  type InsuranceDetail,
  type InsurancePricingTierOut,
} from '@utazzo/shared';
import {
  UzAlert,
  UzBadge,
  UzButton,
  UzCard,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
  UzTabs,
  UzTextarea,
} from '@utazzo/ui';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useCreateInsurance,
  useCreateInsurancePricingTier,
  useDeleteInsurancePricingTier,
  useInsuranceDetail,
  useInsurancePricingTiers,
  useUpdateInsurance,
  useUpdateInsurancePricingTier,
} from '../use-insurance';

import { CountryPicker, CurrencyPicker, SupplierPicker } from '@/components/pickers';
import { CatalogEditorSkeleton } from '@/components/skeletons';
import { RichTextEditor } from '@/features/website-cms/components/RichTextEditor';
import { useListReturn } from '@/hooks/use-list-return';
import { useSaveShortcut } from '@/hooks/use-save-shortcut';

/**
 * Full-page Insurance edit, mirrors VisaEditPage in structure. Sticky top bar
 * that breaks out of the scroll container's padding (flush under the app
 * header), Cmd+S / Ctrl+S saves, tabs for Details / Pricing tiers / Content,
 * and a right sidebar carrying Status + Vendor + Validity + SEO cards.
 *
 *   /insurance/new          mode=create
 *   /insurance/:id/edit     mode=edit
 */

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const INSURANCE_TYPE_OPTIONS = [
  { value: 'SINGLE_TRIP', label: 'Single trip' },
  { value: 'ANNUAL_MULTI_TRIP', label: 'Annual multi-trip' },
  { value: 'STUDENT', label: 'Student' },
  { value: 'SENIOR', label: 'Senior' },
  { value: 'GROUP', label: 'Group' },
  { value: 'ADVENTURE', label: 'Adventure' },
  { value: 'MEDICAL_ONLY', label: 'Medical only' },
];

const COVERAGE_SCOPE_OPTIONS = [
  { value: 'DOMESTIC', label: 'Domestic' },
  { value: 'INTERNATIONAL', label: 'International' },
  { value: 'WORLDWIDE', label: 'Worldwide' },
  { value: 'WORLDWIDE_EXCLUDING_US_CANADA', label: 'Worldwide (excl. US/Canada)' },
];

interface InsuranceFormValues {
  name: string;
  slug: string;
  status: string;
  insuranceType: string;
  coverageScope: string;
  maxCoverageDays: number;
  perPersonPerDay: boolean;
  coverageAmountLabel: string;
  targetCountryId: string | null;
  minAge: number;
  maxAge: number;
  requiredDocumentsText: string;
  eligibleNationalitiesText: string;
  excludedNationalitiesText: string;
  vendorSupplierId: string | null;
  contractRef: string;
  policyNumberFormat: string;
  shortDescription: string;
  longDescriptionHtml: string;
  termsHtml: string;
  cancellationHtml: string;
  claimProcedureHtml: string;
  validityFrom: string;
  validityTo: string;
  metaTagName: string;
}

const emptyValues: InsuranceFormValues = {
  name: '',
  slug: '',
  status: 'DRAFT',
  insuranceType: 'SINGLE_TRIP',
  coverageScope: 'INTERNATIONAL',
  maxCoverageDays: 30,
  perPersonPerDay: false,
  coverageAmountLabel: '',
  targetCountryId: null,
  minAge: 0,
  maxAge: 0,
  requiredDocumentsText: '',
  eligibleNationalitiesText: '',
  excludedNationalitiesText: '',
  vendorSupplierId: null,
  contractRef: '',
  policyNumberFormat: '',
  shortDescription: '',
  longDescriptionHtml: '',
  termsHtml: '',
  cancellationHtml: '',
  claimProcedureHtml: '',
  validityFrom: '',
  validityTo: '',
  metaTagName: '',
};

const detailToForm = (detail: InsuranceDetail): InsuranceFormValues => ({
  name: detail.name,
  slug: detail.slug ?? '',
  status: detail.status,
  insuranceType: detail.insuranceType,
  coverageScope: detail.coverageScope,
  maxCoverageDays: detail.maxCoverageDays,
  perPersonPerDay: detail.perPersonPerDay,
  coverageAmountLabel: detail.coverageAmountLabel ?? '',
  targetCountryId: detail.targetCountryId,
  minAge: detail.minAge,
  maxAge: detail.maxAge,
  requiredDocumentsText: detail.requiredDocuments.join('\n'),
  eligibleNationalitiesText: detail.eligibleNationalities.join(', '),
  excludedNationalitiesText: detail.excludedNationalities.join(', '),
  vendorSupplierId: detail.vendorSupplierId,
  contractRef: detail.contractRef ?? '',
  policyNumberFormat: detail.policyNumberFormat ?? '',
  shortDescription: detail.shortDescription ?? '',
  longDescriptionHtml: detail.longDescriptionHtml ?? '',
  termsHtml: detail.termsHtml ?? '',
  cancellationHtml: detail.cancellationHtml ?? '',
  claimProcedureHtml: detail.claimProcedureHtml ?? '',
  validityFrom: detail.validityFrom ?? '',
  validityTo: detail.validityTo ?? '',
  metaTagName: detail.metaTagName ?? '',
});

const formToPayload = (values: InsuranceFormValues): CreateInsuranceRequest => {
  const splitList = (text: string): string[] =>
    text
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
  return {
    name: values.name.trim(),
    slug: values.slug.trim() || slugify(values.name.trim()),
    status: values.status as CreateInsuranceRequest['status'],
    insuranceType: values.insuranceType as CreateInsuranceRequest['insuranceType'],
    coverageScope: values.coverageScope as CreateInsuranceRequest['coverageScope'],
    maxCoverageDays: values.maxCoverageDays,
    perPersonPerDay: values.perPersonPerDay,
    coverageAmountLabel: values.coverageAmountLabel.trim() || null,
    targetCountryId: values.targetCountryId,
    // Server derives targetCountryName from the FK selection.
    targetCountryName: '',
    minAge: values.minAge,
    maxAge: values.maxAge,
    requiredDocuments: splitList(values.requiredDocumentsText),
    eligibleNationalities: splitList(values.eligibleNationalitiesText).map((s) => s.toUpperCase()),
    excludedNationalities: splitList(values.excludedNationalitiesText).map((s) => s.toUpperCase()),
    vendorSupplierId: values.vendorSupplierId,
    contractRef: values.contractRef.trim() || null,
    policyNumberFormat: values.policyNumberFormat.trim() || null,
    shortDescription: values.shortDescription.trim() || null,
    longDescriptionHtml: values.longDescriptionHtml.trim() || null,
    termsHtml: values.termsHtml.trim() || null,
    cancellationHtml: values.cancellationHtml.trim() || null,
    claimProcedureHtml: values.claimProcedureHtml.trim() || null,
    validityFrom: values.validityFrom || null,
    validityTo: values.validityTo || null,
    metaTagName: values.metaTagName.trim() || null,
  };
};

/** Compact label+control wrapper. Small footprint - no `UzFormField` render-prop
 *  overhead because the insurance edit needs plain `register(...)` binding. */
const Field = ({
  label,
  hint,
  required,
  className,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <div className={className}>
    <label className="mb-1 flex items-center gap-1 text-xs font-medium text-zinc-700">
      {label}
      {required && <span className="text-rose-600">*</span>}
    </label>
    {children}
    {hint && <p className="mt-1 text-[11px] text-zinc-500">{hint}</p>}
  </div>
);

export const InsuranceEditPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const isCreate = !id;
  const navigate = useNavigate();
  const backToList = useListReturn('/insurance');
  const detailQuery = useInsuranceDetail(id);
  const detail = detailQuery.data;

  const createMut = useCreateInsurance();
  const updateMut = useUpdateInsurance();
  const pending = createMut.isPending || updateMut.isPending;

  const form = useForm<InsuranceFormValues>({ defaultValues: emptyValues });
  const { register, reset, handleSubmit, watch, setValue, formState } = form;

  useEffect(() => {
    if (detail) reset(detailToForm(detail));
  }, [detail, reset]);

  const name = watch('name');
  const slug = watch('slug');
  useEffect(() => {
    if (isCreate && !slug && name) setValue('slug', slugify(name));
  }, [name, slug, isCreate, setValue]);

  const [activeTab, setActiveTab] = useState<'details' | 'tiers'>('details');
  const targetCountryId = watch('targetCountryId');
  const vendorSupplierId = watch('vendorSupplierId');

  const submit = handleSubmit(async (values) => {
    if (!values.slug || !values.slug.trim()) {
      form.setError('slug', { message: 'URL slug is required' });
      setActiveTab('details');
      return;
    }
    const payload = formToPayload(values);
    if (isCreate) {
      const created = await createMut.mutateAsync(payload);
      navigate(`/insurance/${created.id}/edit`, { replace: true });
    } else if (id) {
      await updateMut.mutateAsync({ id, payload });
    }
  });

  // Cmd+S (mac) / Ctrl+S (win/linux) triggers save while this page is mounted.
  useSaveShortcut(() => {
    if (!pending) void submit();
  });

  if (!isCreate && detailQuery.isLoading) return <CatalogEditorSkeleton />;
  if (!isCreate && detailQuery.isError)
    return (
      <div className="p-6">
        <UzAlert tone="destructive" title="Failed to load insurance">
          {(detailQuery.error as Error)?.message ?? 'Unknown error.'}
        </UzAlert>
      </div>
    );

  const statusDot =
    detail?.status === 'ACTIVE'
      ? 'bg-emerald-500'
      : detail?.status === 'DRAFT' || detail?.status === 'PENDING_APPROVAL'
        ? 'bg-amber-500'
        : 'bg-zinc-400';
  const statusText =
    detail?.status === 'ACTIVE'
      ? 'Active'
      : detail?.status === 'DRAFT'
        ? 'Draft'
        : detail?.status === 'PENDING_APPROVAL'
          ? 'Pending approval'
          : detail?.status === 'INACTIVE'
            ? 'Inactive'
            : detail?.status === 'ARCHIVED'
              ? 'Archived'
              : '';

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      {/* Sticky top bar - breaks out of the scroll container's padding so it sits
          flush under the app header and stays solid (content never shows through).
          Mirrors visa's edit page exactly. */}
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={backToList}
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          aria-label="Back to insurance"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">Insurance</span>
          <span className="text-zinc-300" aria-hidden>
            /
          </span>
          <span className="truncate text-sm font-semibold tracking-tight text-zinc-900">
            {isCreate ? 'Add insurance' : detail ? detail.name : 'Edit'}
          </span>
        </div>
        <span className="flex-1" />
        {!isCreate && detail && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
            <span className={'h-1.5 w-1.5 rounded-full ' + statusDot} aria-hidden />
            {statusText}
          </span>
        )}
        <UzButton variant="ghost" size="sm" onClick={backToList} disabled={pending}>
          Cancel
        </UzButton>
        <UzButton size="sm" onClick={submit} loading={pending}>
          <span>{isCreate ? 'Create' : 'Save'}</span>
          <kbd className="ml-2 hidden h-4 items-center rounded border border-zinc-300/60 bg-white/15 px-1 font-mono text-[10px] text-current/80 sm:inline-flex">
            ⌘S
          </kbd>
        </UzButton>
      </div>

      <div className="mx-auto w-full max-w-6xl min-w-0 px-6 py-6 lg:px-8">
        {!isCreate && detail && (
          <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
            {detail.isApprovedByAdmin && (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <span className="h-1 w-1 rounded-full bg-emerald-500" aria-hidden />
                Admin approved
              </span>
            )}
            {detail.isApprovedByModerator && (
              <span className="inline-flex items-center gap-1 text-sky-700">
                <span className="h-1 w-1 rounded-full bg-sky-500" aria-hidden />
                Moderator approved
              </span>
            )}
            {formState.isDirty && (
              <span className="inline-flex items-center gap-1 text-amber-700">
                <span className="h-1 w-1 rounded-full bg-amber-500" aria-hidden />
                Unsaved changes
              </span>
            )}
          </div>
        )}

        <UzTabs
          defaultValue="details"
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'details' | 'tiers')}
          tabs={[
            {
              value: 'details',
              label: 'Details',
              content: (
                <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                  {/* Main column */}
                  <div className="min-w-0 space-y-6">
                    <UzCard title="Identity">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="Name" required>
                          <UzInput
                            {...register('name', { required: true })}
                            placeholder="Bajaj Travel Elite"
                          />
                        </Field>
                        <Field label="Slug" required>
                          <UzInput {...register('slug')} placeholder="bajaj-travel-elite" />
                        </Field>
                        <Field label="Insurance type">
                          <UzSelect
                            value={watch('insuranceType')}
                            onValueChange={(v) =>
                              setValue('insuranceType', v, { shouldDirty: true })
                            }
                            options={INSURANCE_TYPE_OPTIONS}
                          />
                        </Field>
                        <Field label="Coverage scope">
                          <UzSelect
                            value={watch('coverageScope')}
                            onValueChange={(v) =>
                              setValue('coverageScope', v, { shouldDirty: true })
                            }
                            options={COVERAGE_SCOPE_OPTIONS}
                          />
                        </Field>
                        <Field label="Max coverage days">
                          <UzNumberInput
                            value={watch('maxCoverageDays')}
                            onValueChange={(v) => setValue('maxCoverageDays', v ?? 0)}
                            min={0}
                            max={3650}
                          />
                        </Field>
                        <Field
                          label="Charge per person per day"
                          hint="When on, the pricing tier's fee is a DAILY rate: itinerary cost = fee x travellers x trip days + agency charge. Off = flat fee per traveller."
                        >
                          <UzSwitch
                            checked={watch('perPersonPerDay')}
                            onCheckedChange={(v) =>
                              setValue('perPersonPerDay', v === true, { shouldDirty: true })
                            }
                          />
                        </Field>
                        <Field
                          label="Coverage amount"
                          hint="Free-text label shown on the docket (e.g. USD 100,000)"
                        >
                          <UzInput {...register('coverageAmountLabel')} placeholder="USD 100,000" />
                        </Field>
                      </div>
                    </UzCard>

                    <UzCard title="Target & eligibility">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field
                          label="Target country"
                          hint="Leave empty for any country"
                          className="sm:col-span-2"
                        >
                          <CountryPicker
                            label=""
                            value={targetCountryId}
                            onValueChange={(v) =>
                              setValue('targetCountryId', v, { shouldDirty: true })
                            }
                          />
                        </Field>
                        <Field label="Min age (0 = no minimum)">
                          <UzNumberInput
                            value={watch('minAge')}
                            onValueChange={(v) => setValue('minAge', v ?? 0)}
                            min={0}
                            max={120}
                          />
                        </Field>
                        <Field label="Max age (0 = no maximum)">
                          <UzNumberInput
                            value={watch('maxAge')}
                            onValueChange={(v) => setValue('maxAge', v ?? 0)}
                            min={0}
                            max={120}
                          />
                        </Field>
                        <Field
                          label="Eligible nationalities"
                          hint="ISO-2 codes, comma-separated (e.g. IN, US, GB). Empty = any."
                          className="sm:col-span-2"
                        >
                          <UzInput
                            {...register('eligibleNationalitiesText')}
                            placeholder="IN, US, GB"
                          />
                        </Field>
                        <Field
                          label="Excluded nationalities"
                          hint="ISO-2 codes, comma-separated"
                          className="sm:col-span-2"
                        >
                          <UzInput
                            {...register('excludedNationalitiesText')}
                            placeholder="IR, KP"
                          />
                        </Field>
                      </div>
                    </UzCard>

                    <UzCard title="Documentation">
                      <Field
                        label="Required documents"
                        hint="One per line. Shown to ops on the claim workflow."
                      >
                        <UzTextarea
                          {...register('requiredDocumentsText')}
                          rows={4}
                          placeholder={
                            'Passport copy\nTravel booking confirmation\nMedical certificate'
                          }
                        />
                      </Field>
                    </UzCard>

                    <UzCard title="Content">
                      <div className="space-y-4">
                        <Field label="Short description" hint="Card / picker preview text">
                          <UzTextarea {...register('shortDescription')} rows={2} />
                        </Field>
                        <Field label="Full description">
                          <RichTextEditor
                            placeholder="Multi-paragraph description with formatting..."
                            value={textToHtml(watch('longDescriptionHtml'))}
                            onChange={(html) =>
                              setValue('longDescriptionHtml', html, { shouldDirty: true })
                            }
                            disabled={pending}
                            folder="insurance"
                            size="compact"
                          />
                        </Field>
                        <Field label="Terms (HTML)">
                          <UzTextarea {...register('termsHtml')} rows={4} />
                        </Field>
                        <Field label="Cancellation policy (HTML)">
                          <UzTextarea {...register('cancellationHtml')} rows={4} />
                        </Field>
                        <Field label="Claim procedure (HTML)">
                          <UzTextarea {...register('claimProcedureHtml')} rows={4} />
                        </Field>
                      </div>
                    </UzCard>
                  </div>

                  {/* Right sidebar (sticky) */}
                  <aside className="space-y-6 self-start lg:sticky lg:top-8">
                    <UzCard title="Publish status">
                      <Field label="Status">
                        <UzSelect
                          value={watch('status')}
                          onValueChange={(v) => setValue('status', v, { shouldDirty: true })}
                          options={STATUS_OPTIONS}
                        />
                      </Field>
                    </UzCard>

                    <UzCard title="Vendor / underwriter">
                      <div className="space-y-4">
                        <Field label="Underwriter" hint="Insurance supplier from the master">
                          <SupplierPicker
                            label=""
                            value={vendorSupplierId}
                            onValueChange={(v) =>
                              setValue('vendorSupplierId', v, { shouldDirty: true })
                            }
                          />
                        </Field>
                        <Field label="Contract reference">
                          <UzInput {...register('contractRef')} placeholder="Optional" />
                        </Field>
                        <Field label="Policy number format" hint="e.g. UTZ-INS-{YYYY}-{seq}">
                          <UzInput {...register('policyNumberFormat')} />
                        </Field>
                      </div>
                    </UzCard>

                    <UzCard title="Validity window">
                      <div className="space-y-4">
                        <Field label="Valid from">
                          <UzInput type="date" {...register('validityFrom')} />
                        </Field>
                        <Field label="Valid to">
                          <UzInput type="date" {...register('validityTo')} />
                        </Field>
                      </div>
                    </UzCard>

                    <UzCard title="SEO">
                      <Field label="Meta tag name">
                        <UzInput {...register('metaTagName')} placeholder="Optional" />
                      </Field>
                    </UzCard>
                  </aside>
                </div>
              ),
            },
            ...(isCreate
              ? []
              : [
                  {
                    value: 'tiers' as const,
                    label: `Pricing tiers${detail ? ` (${detail.pricingTiers.length})` : ''}`,
                    content: id ? <PricingTiersTab insuranceId={id} /> : <div />,
                  },
                ]),
          ]}
        />
      </div>
    </div>
  );
};

// ==================== Pricing tiers tab ====================

interface TierFormState {
  tierName: string;
  coverageDays: number;
  currencyId: string | null;
  feePerApplicant: number;
  agencyCharge: number;
  markupType: 'PERCENT' | 'FLAT' | null;
  markupValue: number;
  isActive: boolean;
  sortOrder: number;
}

const emptyTier: TierFormState = {
  tierName: '',
  coverageDays: 0,
  currencyId: null,
  feePerApplicant: 0,
  agencyCharge: 0,
  markupType: null,
  markupValue: 0,
  isActive: true,
  sortOrder: 0,
};

const MARKUP_TYPE_OPTIONS = [
  { value: '', label: 'No markup' },
  { value: 'PERCENT', label: 'Percent (%)' },
  { value: 'FLAT', label: 'Flat (raw amount)' },
];

const PricingTiersTab = ({ insuranceId }: { insuranceId: string }): React.JSX.Element => {
  const tiersQuery = useInsurancePricingTiers(insuranceId);
  const createTier = useCreateInsurancePricingTier();
  const updateTier = useUpdateInsurancePricingTier();
  const deleteTier = useDeleteInsurancePricingTier();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [draft, setDraft] = useState<TierFormState>(emptyTier);

  const tiers = tiersQuery.data ?? [];
  const editingTier = useMemo(
    () => (editingId ? (tiers.find((t) => t.id === editingId) ?? null) : null),
    [tiers, editingId],
  );

  useEffect(() => {
    if (editingTier)
      setDraft({
        tierName: editingTier.tierName,
        coverageDays: editingTier.coverageDays,
        currencyId: editingTier.currencyId,
        feePerApplicant: editingTier.feePerApplicant,
        agencyCharge: editingTier.agencyCharge,
        markupType: editingTier.markupType,
        markupValue: editingTier.markupValue,
        isActive: editingTier.isActive,
        sortOrder: editingTier.sortOrder,
      });
  }, [editingTier]);

  const startAdd = (): void => {
    setEditingId(null);
    setAddingNew(true);
    setDraft(emptyTier);
  };
  const startEdit = (t: InsurancePricingTierOut): void => {
    setAddingNew(false);
    setEditingId(t.id);
  };
  const cancel = (): void => {
    setAddingNew(false);
    setEditingId(null);
    setDraft(emptyTier);
  };
  const submit = async (): Promise<void> => {
    if (addingNew) {
      const payload: CreateInsurancePricingTierRequest = {
        tierName: draft.tierName.trim(),
        coverageDays: draft.coverageDays,
        currencyId: draft.currencyId,
        feePerApplicant: draft.feePerApplicant,
        agencyCharge: draft.agencyCharge,
        markupType: draft.markupType,
        markupValue: draft.markupValue,
        isActive: draft.isActive,
        sortOrder: draft.sortOrder,
      };
      await createTier.mutateAsync({ insuranceId, payload });
      cancel();
    } else if (editingId) {
      await updateTier.mutateAsync({ insuranceId, tierId: editingId, payload: draft });
      cancel();
    }
  };

  return (
    <div className="space-y-4">
      <UzCard
        title="Pricing tiers"
        description="One row per plan. Charged as `feePerApplicant × pax + agencyCharge`, FX-converted to the itinerary currency."
      >
        {tiersQuery.isLoading ? (
          <div className="py-6 text-sm text-zinc-500">Loading tiers...</div>
        ) : tiers.length === 0 ? (
          <div className="py-6 text-sm text-zinc-500">
            No pricing tiers yet. Add at least one so this insurance can be picked on an itinerary.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {tiers.map((t) => (
              <li key={t.id} className="flex items-center gap-3 py-3">
                <div className="flex flex-1 flex-col">
                  <span className="text-sm font-medium text-zinc-900">
                    {t.tierName}{' '}
                    {t.isActive ? (
                      <UzBadge tone="success" pill>
                        Active
                      </UzBadge>
                    ) : (
                      <UzBadge tone="secondary" pill>
                        Inactive
                      </UzBadge>
                    )}
                  </span>
                  <span className="text-xs text-zinc-500">
                    {t.coverageDays} day cover | {t.currencyCode ?? 'INR'}{' '}
                    {t.feePerApplicant.toLocaleString('en-IN')} per applicant + agency{' '}
                    {t.agencyCharge.toLocaleString('en-IN')}
                    {t.markupType && (
                      <>
                        {' | markup '}
                        {t.markupValue.toLocaleString('en-IN')}
                        {t.markupType === 'PERCENT' ? '%' : ` ${t.currencyCode ?? 'INR'}`}
                      </>
                    )}
                  </span>
                </div>
                <UzButton type="button" variant="ghost" size="sm" onClick={() => startEdit(t)}>
                  Edit
                </UzButton>
                <UzButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteTier.mutate({ insuranceId, tierId: t.id })}
                  disabled={deleteTier.isPending}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </UzButton>
              </li>
            ))}
          </ul>
        )}
        {!addingNew && !editingId && (
          <UzButton type="button" variant="outline" size="sm" className="mt-3" onClick={startAdd}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add pricing tier
          </UzButton>
        )}
      </UzCard>

      {(addingNew || editingId) && (
        <UzCard title={addingNew ? 'New pricing tier' : 'Edit pricing tier'}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Tier name" required>
              <UzInput
                value={draft.tierName}
                onChange={(e) => setDraft({ ...draft, tierName: e.target.value })}
                placeholder="Standard / Premium / Comprehensive"
              />
            </Field>
            <Field label="Coverage days">
              <UzNumberInput
                value={draft.coverageDays}
                onValueChange={(v) => setDraft({ ...draft, coverageDays: v ?? 0 })}
                min={0}
                max={3650}
              />
            </Field>
            <Field label="Currency">
              <CurrencyPicker
                label=""
                value={draft.currencyId}
                onValueChange={(v) => setDraft({ ...draft, currencyId: v })}
              />
            </Field>
            <Field label="Fee per applicant">
              <UzNumberInput
                value={draft.feePerApplicant}
                onValueChange={(v) => setDraft({ ...draft, feePerApplicant: v ?? 0 })}
                min={0}
              />
            </Field>
            <Field label="Agency charge (fixed)">
              <UzNumberInput
                value={draft.agencyCharge}
                onValueChange={(v) => setDraft({ ...draft, agencyCharge: v ?? 0 })}
                min={0}
              />
            </Field>
            <Field
              label="Markup type"
              hint="Percent = % of base cost. Flat = raw amount added on top."
            >
              <UzSelect
                value={draft.markupType ?? ''}
                onValueChange={(v) =>
                  setDraft({
                    ...draft,
                    markupType: v === 'PERCENT' || v === 'FLAT' ? v : null,
                  })
                }
                options={MARKUP_TYPE_OPTIONS}
              />
            </Field>
            <Field label="Markup value">
              <UzNumberInput
                value={draft.markupValue}
                onValueChange={(v) => setDraft({ ...draft, markupValue: v ?? 0 })}
                min={0}
                disabled={draft.markupType === null}
              />
            </Field>
            <Field label="Sort order">
              <UzNumberInput
                value={draft.sortOrder}
                onValueChange={(v) => setDraft({ ...draft, sortOrder: v ?? 0 })}
                min={0}
              />
            </Field>
            <Field label="Active">
              <UzSwitch
                checked={draft.isActive}
                onCheckedChange={(c) => setDraft({ ...draft, isActive: c })}
              />
            </Field>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <UzButton type="button" variant="outline" size="sm" onClick={cancel}>
              Cancel
            </UzButton>
            <UzButton
              type="button"
              size="sm"
              onClick={submit}
              loading={createTier.isPending || updateTier.isPending}
              disabled={!draft.tierName.trim()}
            >
              {addingNew ? 'Add tier' : 'Save tier'}
            </UzButton>
          </div>
        </UzCard>
      )}
    </div>
  );
};
